import React, { FC, ReactNode, useMemo } from 'react';
import { tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { RenderComments, RenderTags, RenderVotes } from '../../canvas/visualization/RenderAnnotations';
import { RenderBounds, RenderBox, RenderConcealed, RenderConcealedEffect, RenderCounter, RenderGraffiti, RenderHeadline, RenderInner, usePose, usePoseEffect, useUser } from '../../canvas/visualization/RenderHelpers';
import { RenderRankingMedal } from '../../canvas/visualization/RenderRankingMedal';
import { RenderText } from '../../canvas/visualization/RenderText';
import { IsConcealedHintVisible } from '../conceal/Concealable';
import { VoteRankAttr } from '../voting/Vote';
import { IndexCard, } from './IndexCard';

const usePoseDerivatives = (object: IndexCard) => {
    const p = usePose(object);
    return useMemo(() => ({
        highlighted: !!(p.pressed || p.targeted || p.selected) && !p.beingManipulated,
        selected: p.selected,
        editing: p.beingEdited,
    }), [p]);
};

const RenderDesign = React.memo<{ color: string, highlighted: boolean, selected: boolean }>(({ color, highlighted, selected }) => {
    if (!color) return null;
    const border = tintSafe(selected ? .5 : .75, Colors.textLight);
    return <RenderBox
        inset="0"
        background="#fff"
        borderRadius="2px"
        border={highlighted ? `solid 2px ${border}` : null}
        boxShadow="0px 3px 10px 0 rgba(0, 0, 0, 0.1)"
    />;
});

const RenderStripes = React.memo<{ bounds: Rect, color: string }>(({ bounds, color }) => {
    color = tintSafe(.5, color);
    const style = { position: 'absolute' as const, ...bounds.topLeft().toCSS() };
    const { width, height } = bounds;
    return <svg style={style} width={width} height={height}>
        <defs>
            <pattern id="lines" x={0} y={0} width={width} height={25} patternUnits="userSpaceOnUse" >
                <line x1={1} y1={0} x2={width - 1} y2={0} stroke="#DDD" strokeWidth="2" />
            </pattern>
        </defs>
        <g>
            <rect width="100%" height="100%" fill="url(#lines)" />
            <line x1={1} y1={0} x2={width - 1} y2={0} stroke={color} strokeWidth="4" />
        </g>
    </svg>;
});

export const IndexCardVisual: FC<{ object: IndexCard, children?: ReactNode }> = ({ object, children }) => {

    const { highlighted, selected, editing } = usePoseDerivatives(object);
    const color = object.color;
    const user = useUser(object);

    const showUser = !editing && highlighted && !!user && !IsConcealedHintVisible.get(object);
    const showHint = !editing && IsConcealedHintVisible.get(object);

    const flipEffect = usePoseEffect(object, 'flip');

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderDesign {...{ color, highlighted, selected }} />
                <RenderStripes bounds={object.layout.content.bounds} color={color} />
                <RenderGraffiti object={object} />
                {!editing && <RenderText {...object.layout.title} value={object.title} formatOpts={['mention', 'bold', 'italic']} />}
                {!editing && <RenderText {...object.layout.content} value={object.content} formatOpts={['mention', 'bold', 'italic', 'list']} />}
                <RenderComments object={object} />
                <RenderVotes object={object} />
                <RenderTags object={object} />
                <RenderRankingMedal value={VoteRankAttr.get(object)} show={!highlighted} />
                {showUser &&
                    <RenderHeadline>
                        <RenderCounter user={user} />
                    </RenderHeadline>
                }
                {showHint &&
                    <RenderHeadline>
                        <RenderConcealed object={object} />
                    </RenderHeadline>
                }
                <RenderConcealedEffect effect={flipEffect} />
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};
